"use client";

import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { useCustomersList } from "@/hooks/api/useCustomers";
import { buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export default function CustomersListPage() {
  const [search, setSearch] = useState("");
  const [tier, setTier] = useState("");
  const [outstandingOnly, setOutstandingOnly] = useState(false);
  const debounced = useMemo(() => search.trim(), [search]);

  const { data, isLoading, isError } = useCustomersList({
    search: debounced,
    tier,
    outstandingOnly,
  });

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Customers</h1>
        <p className="text-sm text-muted-foreground">
          Search, tier filter, outstanding indicator.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <div className="grid gap-2 min-w-[200px] flex-1">
            <Label htmlFor="q">Search</Label>
            <Input
              id="q"
              placeholder="Name, phone, GSTIN"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="grid gap-2 w-[160px]">
            <Label>Tier</Label>
            <Select
              value={tier || "__all"}
              onValueChange={(v) => setTier(!v || v === "__all" ? "" : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All tiers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All tiers</SelectItem>
                <SelectItem value="bronze">Bronze</SelectItem>
                <SelectItem value="silver">Silver</SelectItem>
                <SelectItem value="gold">Gold</SelectItem>
                <SelectItem value="platinum">Platinum</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer pb-2">
            <input
              type="checkbox"
              checked={outstandingOnly}
              onChange={(e) => setOutstandingOnly(e.target.checked)}
              className="rounded border-input"
            />
            Outstanding only
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Directory</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading && (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}
          {isError && (
            <p className="text-sm text-destructive">Failed to load customers.</p>
          )}
          {!isLoading && !isError && (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left">
                  <tr>
                    <th className="p-3 font-medium">Name</th>
                    <th className="p-3 font-medium">Phone</th>
                    <th className="p-3 font-medium">Tier</th>
                    <th className="p-3 font-medium text-right">Points</th>
                    <th className="p-3 font-medium text-right">Outstanding</th>
                    <th className="p-3 w-24" />
                  </tr>
                </thead>
                <tbody>
                  {(data ?? []).map((c) => {
                    const ob = Number(c.outstanding_balance ?? 0);
                    return (
                      <tr key={c.id} className="border-t">
                        <td className="p-3 font-medium">{c.name}</td>
                        <td className="p-3 text-muted-foreground">{c.phone}</td>
                        <td className="p-3 capitalize">{c.loyalty_tier ?? "—"}</td>
                        <td className="p-3 text-right tabular-nums">
                          {c.loyalty_points ?? 0}
                        </td>
                        <td
                          className={cn(
                            "p-3 text-right tabular-nums",
                            ob > 0.01 && "text-amber-700 font-medium",
                          )}
                        >
                          ₹{ob.toFixed(2)}
                        </td>
                        <td className="p-3">
                          <Link
                            to={`/customers/${c.id}`}
                            className={cn(
                              buttonVariants({ variant: "outline", size: "sm" }),
                            )}
                          >
                            Open
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {(data ?? []).length === 0 && (
                <p className="p-6 text-sm text-muted-foreground text-center">
                  No customers match filters.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
